import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useSWR from "swr";

import ExportCSV from "../../ExportCSV";
import {
  DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE,
  DEFAULT_PRICING_REPORT_UNIT,
} from "../../../routes/pricing-reports/constants";
import { retailerPriceHistoryCountByNameFromAggregateRows } from "../../../routes/pricing-reports/pricingReportProductFilter";
import {
  beefAiConfigured,
  fetchDistinctCounts,
  fetchMonthlyAverageByProduct,
  fetchPublishedProducts,
} from "../../../services/beefAiPricing";
import ProductFrequencyCharts from "./ProductFrequencyCharts";
import ProductFrequencyFilters from "./ProductFrequencyFilters";
import ProductFrequencyTable from "./ProductFrequencyTable";
import {
  buildMonthlyFrequencyBarChart,
  buildProductFrequencyRows,
  buildRetailerSpreadBarChart,
  buildTopProductPieChart,
  buildUnderUtilizedCutsPieChart,
} from "./productFrequencyUtils";

const FREQUENCY_CSV_COLUMNS = [
  { key: "productName", label: "Product" },
  { key: "count", label: "Count" },
];

const REPORT_CARD_SX = {
  width: "100%",
  boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
  position: "relative",
  padding: "16px",
  backgroundColor: "#efeeee",
};

const swrOptions = { revalidateOnFocus: false };

function formatFrequencyExportFileName(dateFrom, dateTo) {
  const fromLabel = dateFrom?.isValid?.() ? dateFrom.format("YYYY-MM-DD") : "from";
  const toLabel = dateTo?.isValid?.() ? dateTo.format("YYYY-MM-DD") : "to";
  return `product-frequency-${fromLabel}-to-${toLabel}.csv`;
}

export default function ProductFrequencyReport() {
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(4, "month").startOf("day"));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf("day"));
  const [selectedProduct, setSelectedProduct] = useState(null);

  const startDateForApi = dateFrom && dateFrom.isValid() ? dateFrom.format("YYYY-MM-DD") : "";
  const endDateForApi = dateTo && dateTo.isValid() ? dateTo.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : "";
  const dateRangeIsValid = Boolean(
    startDateForApi &&
      endDateForApi &&
      dateFrom?.isValid() &&
      dateTo?.isValid() &&
      !dateFrom.startOf("day").isAfter(dateTo.endOf("day"))
  );
  const queriesEnabled = beefAiConfigured() && dateRangeIsValid;

  const queryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      providersInclude: [...DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE],
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted: false,
    }),
    [startDateForApi, endDateForApi]
  );

  useEffect(() => {
    setSelectedProduct(null);
  }, [startDateForApi, endDateForApi]);

  const { data: catalogProducts, error: catalogError } = useSWR(
    beefAiConfigured() ? "product-frequency-catalog-with-ref-id" : null,
    () => fetchPublishedProducts({ withRefIdOnly: true }),
    { ...swrOptions, dedupingInterval: 60 * 60 * 1000 }
  );

  const { data: countPayload, error: countsError } = useSWR(
    queriesEnabled ? ["product-frequency-counts", queryFilters] : null,
    async () => {
      const [productRows, retailerRows] = await Promise.all([
        fetchDistinctCounts("product_id", queryFilters),
        fetchDistinctCounts("retailer", queryFilters),
      ]);
      return { productRows, retailerRows };
    },
    { ...swrOptions, keepPreviousData: true }
  );

  const monthlySwrKey =
    queriesEnabled && selectedProduct?.id
      ? ["product-frequency-monthly", selectedProduct.id, queryFilters]
      : null;

  const {
    data: monthlyRows,
    error: monthlyError,
    isLoading: isMonthlyLoading,
  } = useSWR(
    monthlySwrKey,
    () =>
      fetchMonthlyAverageByProduct({
        ...queryFilters,
        productId: selectedProduct.id,
      }),
    swrOptions
  );

  const frequencyRows = useMemo(
    () => buildProductFrequencyRows(catalogProducts, countPayload?.productRows),
    [catalogProducts, countPayload?.productRows]
  );

  const retailerCountByName = useMemo(
    () => retailerPriceHistoryCountByNameFromAggregateRows(countPayload?.retailerRows),
    [countPayload?.retailerRows]
  );

  const topProductChart = useMemo(() => buildTopProductPieChart(frequencyRows), [frequencyRows]);
  const underUtilizedChart = useMemo(
    () => buildUnderUtilizedCutsPieChart(frequencyRows),
    [frequencyRows]
  );
  const retailerSpreadChart = useMemo(
    () => buildRetailerSpreadBarChart(retailerCountByName),
    [retailerCountByName]
  );
  const monthlyChart = useMemo(
    () => buildMonthlyFrequencyBarChart(monthlyRows),
    [monthlyRows]
  );

  const countedExportRows = useMemo(
    () =>
      frequencyRows
        .filter((row) => row.count > 0)
        .map((row) => ({ productName: row.productName, count: row.count })),
    [frequencyRows]
  );

  const handleProductClick = useCallback((row) => {
    setSelectedProduct((current) => (current?.id === row?.id ? null : row));
  }, []);

  const fetchError = catalogError || countsError;
  const isInitialLoading =
    queriesEnabled && !fetchError && (catalogProducts === undefined || countPayload === undefined);

  if (!beefAiConfigured()) {
    return (
      <Box sx={{ py: 1 }}>
        <Alert severity="warning">
          Product / Frequency is not configured. Set `REACT_APP_PRICE_DIRECTUS_URL` and
          `REACT_APP_PRICE_DIRECTUS_TOKEN` for this environment, then reload the page.
        </Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={REPORT_CARD_SX}>
        <ProductFrequencyFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          filterActions={
            <ExportCSV
              columns={FREQUENCY_CSV_COLUMNS}
              data={countedExportRows}
              fileName={formatFrequencyExportFileName(dateFrom, dateTo)}
              buttonText="Export to CSV"
              disabled={!dateRangeIsValid || countedExportRows.length === 0}
            />
          }
        />

        <Stack spacing={2} sx={{ mt: 2 }}>
          {!dateRangeIsValid ? (
            <Alert severity="warning">
              Select a valid From and To date range. From must be on or before To.
            </Alert>
          ) : null}

          {fetchError ? (
            <Alert severity="error">
              {fetchError.message || "Something went wrong while loading product frequency data."}
            </Alert>
          ) : null}

          {dateRangeIsValid ? (
            <>
              <ProductFrequencyCharts
                topProductChart={topProductChart}
                underUtilizedChart={underUtilizedChart}
                retailerSpreadChart={retailerSpreadChart}
                monthlyChart={monthlyChart}
                monthlyProductName={selectedProduct?.productName || ""}
                isMonthlyLoading={Boolean(selectedProduct) && isMonthlyLoading && monthlyRows === undefined}
                monthlyError={monthlyError}
                isLoading={isInitialLoading}
              />
              <ProductFrequencyTable
                rows={frequencyRows}
                isLoading={isInitialLoading}
                selectedProductId={selectedProduct?.id || ""}
                onProductClick={handleProductClick}
              />
            </>
          ) : null}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
